import { clsx } from 'clsx'
import styles from './RangeSlider.module.css'

type RangeSliderProps = {
  min:        number
  max:        number
  step?:      number
  valueMin:   number
  valueMax:   number
  /** Fired with the clamped (min, max) pair — min never exceeds max and vice-versa. */
  onChange:   (min: number, max: number) => void
  disabled?:  boolean
  /** Formats the value labels under the track (e.g. price → "$7.5"). */
  formatValue?: (n: number) => string
  /** Accessible label prefix for the two thumbs. */
  label?:     string
}

/**
 * Dual-handle range slider built on two overlaid native <input type="range"> elements.
 * Native inputs keep this dependency-free and a11y-friendly; the CSS disables pointer
 * events on the tracks and re-enables them only on the thumbs so both handles stay
 * grabbable while sharing the same area.
 */
export function RangeSlider({
  min,
  max,
  step = 1,
  valueMin,
  valueMax,
  onChange,
  disabled = false,
  formatValue,
  label,
}: RangeSliderProps) {
  // Clamp the incoming values into [min, max] and keep lo <= hi for display.
  const lo = Math.min(Math.max(valueMin, min), max)
  const hi = Math.min(Math.max(valueMax, min), max)
  const span = max - min || 1
  const loPct = ((lo - min) / span) * 100
  const hiPct = ((hi - min) / span) * 100

  const fmt = formatValue ?? ((n: number) => String(n))

  // When both thumbs stack on the same spot the top one wins the pointer. The min
  // thumb sits on top by default (so a pair stacked at the max stays draggable left),
  // but at the low end min is pinned against its floor — lift the max thumb above it
  // so the stacked pair can still be pulled apart. Without this it locks (see $4 = $4).
  const liftMax = lo >= hi && hi < max

  return (
    <div className={clsx(styles.wrap, disabled && styles.disabled)}>
      <div className={styles.track}>
        <div
          className={styles.fill}
          style={{ left: `${loPct}%`, right: `${100 - hiPct}%` }}
        />
        <input
          type="range"
          className={clsx(styles.input, styles.inputMin)}
          min={min}
          max={max}
          step={step}
          value={lo}
          disabled={disabled}
          onChange={(e) => onChange(Math.min(Number(e.target.value), hi), hi)}
          aria-label={label ? `${label} — mínimo` : 'Mínimo'}
        />
        <input
          type="range"
          className={clsx(styles.input, styles.inputMax, liftMax && styles.onTop)}
          min={min}
          max={max}
          step={step}
          value={hi}
          disabled={disabled}
          onChange={(e) => onChange(lo, Math.max(Number(e.target.value), lo))}
          aria-label={label ? `${label} — máximo` : 'Máximo'}
        />
      </div>
      <div className={styles.values}>
        <span>{fmt(lo)}</span>
        <span>{fmt(hi)}</span>
      </div>
    </div>
  )
}
